// In App.js in a new project
import 'react-native-reanimated';
import 'react-native-gesture-handler';
import { ErrorBoundary } from '@/components/SharedComponents/ErrorBoundary/ErrorBoundary';
import LoadingIndicator from '@/components/SharedComponents/LoadingIndicator/LoadingIndicator';
import { isPatientApp } from '@/config/constants';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { Platform, StatusBar, StyleSheet, View } from 'react-native';
import { StatusBar as ExpoStatusBar } from 'expo-status-bar';
import 'react-native-get-random-values';
import {
  addSslPinningErrorListener,
  isSslPinningAvailable,
} from 'react-native-ssl-public-key-pinning';
import Toast from 'react-native-toast-message';
import api from './api/axios';
import { localLogoutUser } from './api/services/user';
import { checkAppVersion } from './api/services/versions';
import { RootLayout } from '@/layouts/RootLayout';
import { AppNavigation } from './navigations/AppNavigation';
import { navigate } from '@/navigations/RootNavigation';
import { getAccessToken, serverLogoutUser } from './stores/auth/services';
import { useSessionGuard } from './hooks/useSessionGuard';
import { isLoggingOut } from './hooks/useAuth';
import store, { useStoreState } from './stores/store';
import sslPinning from './utils/sslPinning';
import { initTrackers } from './utils/utils';
import { useFont } from './hooks/useFont';
import { useStartupChecks } from './hooks/useStartupChecks';
import { logError } from './utils/logHandling';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import { setUser, wrap } from '@sentry/react-native';
import { IS_WEB } from './utils/platform';

initTrackers(Platform.OS);
// initialize SSL Pinning
sslPinning.init();

function MainApp() {
  useStartupChecks();

  const fontsReady = useFont();
  const [isLoading, setIsLoading] = useState(false);
  const [interceptorsAdded, setInterceptorsAdded] = useState(false);

  const { showLoadingSpinner, signedInUser } = useStoreState((state) => state);
  const { showNotification } = useNotification();
  const { setShowLoadingSpinner } = store.getActions();

  const checkStatus = useCallback(async () => {
    try {
      // Check for app new version
      await checkAppVersion();
    } catch (error) {
      logError('Could not get app version', error);
    }
  }, []);

  useLayoutEffect(() => {
    StatusBar.setHidden(true);
  }, []);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined = undefined;
    if (isLoading) {
      timeoutId = setTimeout(() => {
        setShowLoadingSpinner(true);
      }, 300);
    } else {
      timeoutId = setTimeout(() => {
        // Show loading spinner for 500ms to avoid flickering
        setShowLoadingSpinner(false);
      }, 500);
    }

    return () => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [isLoading]);

  useEffect(() => {
    const userId = signedInUser.user?._id;
    if (userId) {
      setUser({ id: userId });
    }
  }, [signedInUser]);

  useEffect(() => {
    // Interceptors for HTTP requests
    // Request interceptors
    if (isSslPinningAvailable()) {
      addSslPinningErrorListener(() => {
        showNotification({
          title: 'SSL Pinning Fehler',
          type: 'error',
          duration: 1000 * 60 * 60,
        });
        // Triggered when an SSL pinning error occurs due to pin mismatch
        // logInfo('pinning error: ' + error.serverHostname + ' PLATFORM ' + Platform.OS);
      });
    }
    const request = api.interceptors.request.use(async (config) => {
      // ignore this API calls because it is too slow or unnecessary to block the UI for it
      let APIS_EXCLUDED_FORM_LOADING_SPINNER = [
        '/api/auth/reset',
        '/api/words/search',
        '/api/sentences/search',
        '/api/texts/search',
        '/api/consent',
        '/api/patients/license-status',
        !isPatientApp ? '/api/permissions' : '',
      ];

      if (!APIS_EXCLUDED_FORM_LOADING_SPINNER.includes(config.url ?? '')) {
        setIsLoading(true);
      }

      // Add token for each request except sign in
      let EXCLUDED_APIS = ['/api/auth/signin'];
      if (
        !EXCLUDED_APIS.includes(config.url ?? '') &&
        store.getState().isAuthenticated
      ) {
        const accessToken = await getAccessToken();
        config.headers['Authorization'] = 'JWT ' + accessToken;
      }
      return config;
    });

    // Response Interceptors
    const response = api.interceptors.response.use(
      (response) => {
        setIsLoading(false);
        return response;
      },
      async (error) => {
        // Handle 403 response
        if (
          error?.response?.status === 403 ||
          error?.response?.status === 401
        ) {
          if (!isLoggingOut() && store.getState().isAuthenticated) {
            showNotification({
              title: 'Sitzung abgelaufen. Bitte melden Sie sich neu an.',
              type: 'error',
            });
            serverLogoutUser({ jwt: (await getAccessToken()) ?? '' });
            localLogoutUser();
            store.getActions().setStayLoggedIn(false);
          }
          !store.getState().isAuthenticated && navigate('Login');
        }

        // Handle error responses
        setIsLoading(false);
        return Promise.reject(error);
      },
    );

    setInterceptorsAdded(true);
    checkStatus();

    return () => {
      api.interceptors.request.eject(request);
      api.interceptors.response.eject(response);
    };
  }, []);

  return (
    <>
      <RootLayout>
        <ExpoStatusBar hidden={true} />
        <LoadingIndicator
          showLoading={showLoadingSpinner || !interceptorsAdded || !fontsReady}
        />
        {interceptorsAdded && (IS_WEB || fontsReady) ? <AppNavigation /> : null}
        <Toast topOffset={70} />
      </RootLayout>
    </>
  );
}

function App() {
  const AppSpinner = useMemo(() => {
    return (
      <View style={styles.container}>
        <LoadingIndicator showLoading />
      </View>
    );
  }, []);

  const isReady = useSessionGuard();
  return (
    <ErrorBoundary root>{isReady ? <MainApp /> : AppSpinner}</ErrorBoundary>
  );
}

// expose store when run in Cypress
if (window.Cypress) {
  window.store = store;
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default wrap(App);
